import React, { useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, Modal, Spin, message } from 'antd';
import {
  CheckCircleFilled,
  FileTextOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getKnowledgeBaseList, type KnowledgeBaseItem } from '../../../../api';
import type { KnowledgeBaseSelection } from '../configAndSendQuestion/KnowledgeBasePickerModal';

interface Props {
  open: boolean;
  messageText: string;
  processName: string;
  value?: string;
  loading?: boolean;
  onConfirm: (selection: KnowledgeBaseSelection) => void;
  onCancel?: () => void;
}

/**
 * 后端 pause 场景下的知识库重选弹窗。
 * 不提供右上角 X / ESC / 遮罩点击关闭，但通过“取消任务”按钮提供异常场景下的逃生口。
 */
const PauseKnowledgeBaseModal: React.FC<Props> = ({
  open,
  messageText,
  processName,
  value,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const [keyword, setKeyword] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  const {
    data: list = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['knowledge-base-list'],
    queryFn: getKnowledgeBaseList,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setSelectedId(value ?? null);
  }, [open, value]);

  const visibleList = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    if (!kw) return list;
    return list.filter(item => {
      const name = (item.name || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      return name.includes(kw) || desc.includes(kw);
    });
  }, [keyword, list]);

  const handleConfirm = () => {
    const item = list.find(i => i.id === selectedId);
    if (!item) {
      messageApi.warning('请先选择知识库');
      return;
    }
    onConfirm({ id: item.id, name: item.name });
  };

  const renderCard = (item: KnowledgeBaseItem) => {
    const selected = item.id === selectedId;
    return (
      <div
        key={item.id}
        role="button"
        tabIndex={0}
        onClick={() => setSelectedId(item.id)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setSelectedId(item.id);
          }
        }}
        className={[
          'relative cursor-pointer rounded-md border px-3 py-2.5 transition-colors',
          selected
            ? 'border-[#2094f0] bg-[#f0f9ff]'
            : 'border-[#e5e7eb] bg-white hover:border-[#2094f0]',
        ].join(' ')}
      >
        {selected && (
          <CheckCircleFilled className="absolute right-2 top-2 text-[#2094f0]" />
        )}
        <div className="flex items-start gap-2">
          <FileTextOutlined className="mt-1 shrink-0 text-[#2094f0]" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-[#111827]">
              {item.name}
            </div>
            {item.description && (
              <div className="mt-0.5 line-clamp-2 text-xs text-[#6b7280]">
                {item.description}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title="流程已暂停：请选择知识库"
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={720}
      footer={
        <div className="flex items-center justify-between">
          {/* 取消按钮：接口持续异常、列表为空等场景下的逃生口 */}
          {onCancel ? (
            <Button type="text" danger size="small" onClick={onCancel}>
              取消任务
            </Button>
          ) : (
            <div />
          )}
          <Button type="primary" loading={loading} onClick={handleConfirm}>
            确认并继续
          </Button>
        </div>
      }
    >
      {contextHolder}
      <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <div>{messageText}</div>
        <div className="mt-1">
          当前过程：
          <span className="font-medium">{processName || '未知过程'}</span>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-[#bfbfbf]" />}
          placeholder="搜索知识库名称或描述"
          value={keyword}
          onChange={event => setKeyword(event.target.value)}
        />
        <div className="h-[420px] overflow-auto rounded-md border border-[#e5e7eb] bg-[#fafbfc] p-2">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spin />
            </div>
          ) : isError ? (
            <div className="flex h-full flex-col items-center justify-center gap-2">
              <span className="text-sm text-[#ef4444]">
                加载失败：{(error as Error)?.message || '未知错误'}
              </span>
              <Button size="small" onClick={() => refetch()}>
                重试
              </Button>
            </div>
          ) : visibleList.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={keyword ? '未搜到匹配的知识库' : '暂无知识库'}
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {visibleList.map(renderCard)}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PauseKnowledgeBaseModal;
